import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faShield,
  faCircleCheck,
  faCircleXmark,
  faRightToBracket,
  faCalendarDays,
  faTriangleExclamation,
  faSpinner,
  faEye,
  faEyeSlash,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { platformAuthService } from '@/services/platform-auth.service';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/shared/FormField';
import { SkeletonProfile } from '@/components/skeletons';
import { getApiErrorMessage } from '@/lib/api-error';
import axios from 'axios';

/**
 * Phase 12.8 — Platform Profile.
 *
 * Design of record: `PHASE_12_8_DESIGN_READINESS_PACKAGE_20260828.md` §B,
 * approved under Owner decision **OQ-1 = A (Profile-only)**. Platform Settings
 * is DEFERRED by the same decision and is not touched here.
 *
 * ── No new backend, no new permission ──────────────────────────────────────
 * Both endpoints behind this page have been deployed since Phase 12.1B and are
 * `@PlatformAuthenticatedOnly()` — self-scoped, deliberately permission-free.
 * That is not an oversight being tolerated: gating self-service would leave the
 * `platform-auditor`, who holds no manage permission at all, unable to rotate
 * their own credential. Phase 12.6 excluded self-service change-password from
 * the staff-administration page precisely so it would live here
 * (`PLATFORM_USERS_DESIGN.md:732`).
 *
 * ── The read costs nothing ─────────────────────────────────────────────────
 * `PlatformAuthInitializer` has already called `GET /platform/auth/me` and
 * hydrated `platform-auth.store` with the full `PlatformUserProfileDto`. This
 * page therefore issues **no request on mount** — a second `/auth/me` here
 * would be a redundant round trip for data already in memory. The three fields
 * the old placeholder never rendered (`isActive`, `lastLoginAt`, `createdAt`)
 * were already present on the type and simply unused.
 */

/** Matches the platform DTO's `@MinLength(12)`, not the 8-char staff policy. */
const PLATFORM_PASSWORD_MIN_LENGTH = 12;

/**
 * Date + time, matching `formatCompanyDateTime`'s convention on the Companies
 * detail page — "when exactly" matters for a sign-in timestamp.
 */
function formatProfileDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * `lastLoginAt: null` is a real state — an account that has never signed in.
 * It renders as "Never", never as an em dash and never as a fabricated date.
 */
function formatLastSignIn(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return 'Never';
  return formatProfileDateTime(iso);
}

interface DetailRowProps {
  icon: typeof faUser;
  label: string;
  value: string;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-center gap-3">
      <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="text-sm font-medium break-words text-foreground">{value}</dd>
      </div>
    </div>
  );
}

export function PlatformProfilePage() {
  const navigate = useNavigate();
  const user = usePlatformAuthStore((s) => s.user);
  const clearAuth = usePlatformAuthStore((s) => s.clearAuth);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * The store is hydrated before this route renders — `PlatformAuthInitializer`
   * re-validates on startup and `PlatformProtectedRoute` redirects when it
   * fails. A null user here means that re-validation is still in flight, so the
   * governed skeleton is shown rather than an identity card full of dashes.
   */
  if (!user) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Profile</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Your platform account details</p>
        </div>
        <SkeletonProfile />
      </div>
    );
  }

  const displayName = user.displayName ?? '—';
  const initials =
    displayName
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase() || 'P';

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!currentPassword) e.currentPassword = 'Enter your current password';
    if (!newPassword) {
      e.newPassword = 'Enter a new password';
    } else if (newPassword.length < PLATFORM_PASSWORD_MIN_LENGTH) {
      e.newPassword = `New password must be at least ${PLATFORM_PASSWORD_MIN_LENGTH} characters`;
    }
    // Caught here and never sent — the server has no confirm field.
    if (!confirmPassword) {
      e.confirmPassword = 'Re-enter the new password';
    } else if (newPassword && confirmPassword !== newPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    return e;
  };

  /** Cleared for hygiene after a failure; the rest of the form is preserved. */
  const clearPasswordFields = (): void => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setFormError(null);
    setSuccess(null);

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const result = await platformAuthService.changePassword(currentPassword, newPassword);
      // The server has already revoked every session, including this one, so
      // there is no valid state to stay in. Surface its message, then leave.
      setSuccess(result.message);
      clearPasswordFields();
      clearAuth();
      navigate(PLATFORM_ROUTES.LOGIN, { replace: true });
    } catch (err) {
      // 401 here means "wrong current password" — the endpoint documents it as
      // ApiUnauthorizedResponse, NOT 400. The platform API client leaves this
      // route alone, so nothing has cleared the session behind our back.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setFormError('Your current password is incorrect.');
      } else if (axios.isAxiosError(err) && err.response?.status === 400) {
        setFormError(
          getApiErrorMessage(err, 'That new password does not meet the platform password policy.'),
        );
      } else {
        setFormError('Something went wrong. Your password was not changed.');
      }
      clearPasswordFields();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Profile</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Your platform account details</p>
      </div>

      {/* ── Card 1 — Identity ─────────────────────────────────────────── */}
      <div className="flex flex-col items-start gap-4 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center">
        <div
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-xl font-semibold text-primary-foreground"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold break-words text-foreground">{displayName}</p>
          {user.email && (
            <div className="mt-1 flex items-center gap-1.5 text-sm break-all text-muted-foreground">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 shrink-0" />
              {user.email}
            </div>
          )}
          {user.role && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-primary">
              <FontAwesomeIcon icon={faShield} className="h-3.5 w-3.5 shrink-0" />
              {user.role}
            </div>
          )}
        </div>
      </div>

      {/* ── Card 2 — Account Details ──────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Account Details</h2>
        <dl className="space-y-3">
          <DetailRow icon={faUser} label="Display Name" value={displayName} />
          <DetailRow icon={faEnvelope} label="Email" value={user.email ?? '—'} />
          <DetailRow icon={faShield} label="Platform Role" value={user.role ?? '—'} />
          <DetailRow
            icon={user.isActive ? faCircleCheck : faCircleXmark}
            label="Status"
            value={user.isActive ? 'Active' : 'Inactive'}
          />
          <DetailRow
            icon={faRightToBracket}
            label="Last Sign-In"
            value={formatLastSignIn(user.lastLoginAt)}
          />
          <DetailRow
            icon={faCalendarDays}
            label="Account Created"
            value={formatProfileDateTime(user.createdAt)}
          />
        </dl>
      </div>

      {/* ── Card 3 — Change Password ──────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5 text-muted-foreground" />
          Change Password
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Minimum {PLATFORM_PASSWORD_MIN_LENGTH} characters.
        </p>

        <form onSubmit={handleSubmit} noValidate className="max-w-md space-y-5">
          <FormField
            label="Current Password"
            htmlFor="profile-current-password"
            required
            error={errors.currentPassword}
          >
            <div className="relative">
              <Input
                id="profile-current-password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                aria-required="true"
                disabled={submitting}
                className="min-h-[44px] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} className="h-4 w-4" />
              </button>
            </div>
          </FormField>

          <FormField
            label="New Password"
            htmlFor="profile-new-password"
            required
            error={errors.newPassword}
          >
            <div className="relative">
              <Input
                id="profile-new-password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                aria-required="true"
                disabled={submitting}
                className="min-h-[44px] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                aria-label={showNew ? 'Hide new password' : 'Show new password'}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} className="h-4 w-4" />
              </button>
            </div>
          </FormField>

          <FormField
            label="Confirm New Password"
            htmlFor="profile-confirm-password"
            required
            error={errors.confirmPassword}
          >
            <Input
              id="profile-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              aria-required="true"
              disabled={submitting}
              className="min-h-[44px]"
            />
          </FormField>

          {/*
            The destructive effect is on the caller's OWN session, so the warning
            is more useful in context than behind a confirmation dialog. It is
            visible at all times, before submission — not reported afterwards.
          */}
          <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
            />
            <span>
              Changing your password signs you out of every device, including this one. You will
              need to sign in again.
            </span>
          </div>

          <div aria-live="polite">
            {formError && (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            )}
            {success && <p className="text-sm text-foreground">{success}</p>}
          </div>

          <Button
            type="submit"
            className="min-h-[44px] w-full sm:w-auto"
            disabled={submitting}
            aria-disabled={submitting}
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                Changing password...
              </>
            ) : (
              'Change Password'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
